import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'

function useScrollShadows(ref: React.RefObject<HTMLDivElement | null>) {
  const [showLeft, setShowLeft] = React.useState(false)
  const [showRight, setShowRight] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return

    function update() {
      if (!el) return
      const { scrollLeft, scrollWidth, clientWidth } = el
      setShowLeft(scrollLeft > 0)
      setShowRight(scrollLeft + clientWidth < scrollWidth - 1)
    }

    update()
    el.addEventListener('scroll', update, { passive: true })

    const observer = new ResizeObserver(update)
    observer.observe(el)

    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [ref])

  return { showLeft, showRight }
}

function ScrollShadow({ side, visible }: { side: 'left' | 'right'; visible: boolean }) {
  const isLeft = side === 'left'
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute top-0 bottom-0 z-10 w-20 transition-opacity duration-200',
        isLeft ? 'left-0' : 'right-0',
        visible ? 'opacity-100' : 'opacity-0'
      )}
      // Fades from the surface the table sits on: a Card sets --scroll-shadow-color to its own fill.
      style={{
        background: isLeft
          ? 'linear-gradient(to right, var(--scroll-shadow-color, var(--color-background)), transparent)'
          : 'linear-gradient(to left, var(--scroll-shadow-color, var(--color-background)), transparent)',
      }}
    />
  )
}

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const { showLeft, showRight } = useScrollShadows(scrollRef)

  return (
    <div data-slot="table-container" className="relative w-full">
      <ScrollShadow side="left" visible={showLeft} />
      <ScrollShadow side="right" visible={showRight} />
      <div ref={scrollRef} className="w-full overflow-x-auto">
        <table data-slot="table" className={cn('w-full caption-bottom text-sm', className)} {...props} />
      </div>
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  // A header row takes no click, so it must not light up like the body rows do.
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b [&_tr]:hover:bg-transparent', className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn('hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors', className)}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-foreground h-10 px-2 py-3 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

/** Which way a column sorts its table; the values are aria-sort's. */
enum SortDirection {
  ASCENDING = 'ascending',
  DESCENDING = 'descending',
}

const SORT_ICONS = {
  [SortDirection.ASCENDING]: ArrowUp,
  [SortDirection.DESCENDING]: ArrowDown,
}

interface SortableTableHeadProps extends React.ComponentProps<'th'> {
  /** Unset while the table sorts by another column. */
  direction?: SortDirection
  onSort: () => void
}

/** A column header whose label is a button that sorts the table by it. */
function SortableTableHead({ direction, onSort, children, ...props }: SortableTableHeadProps) {
  const Icon = direction ? SORT_ICONS[direction] : ArrowUpDown
  return (
    <TableHead aria-sort={direction ?? 'none'} {...props}>
      {/* Negative margins keep the label where a plain header's sits and the row its height. */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-mx-2 -my-1 h-auto px-2 py-1 has-[>svg]:px-2"
        onClick={onSort}
      >
        {children}
        <Icon className={cn('size-3.5', !direction && 'text-ink-300')} />
      </Button>
    </TableHead>
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'p-2 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

export {
  SortableTableHead,
  SortDirection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
